/* eslint-disable no-nested-ternary */
import {
  Box, Grid, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { indigo, yellow } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { ContentMembership } from '@optimus/models';
import { MetaItem } from '../../../common/models/content/Content';
import { TestIds } from '../../../assets/testIds';

interface ContentCardHeaderProps {
  categoryText?: string | null,
  metadata?: MetaItem[];
  contentShortDescription: string,
  contentText: string,
  lowestMembershipRequired?: ContentMembership,
}

export default function ContentCardHeader(props: ContentCardHeaderProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktopScreen = useMediaQuery(() => theme.breakpoints.up('sm'));
  const getMetaData = () => {
    if (!props.metadata || props.metadata.length === 0) return '';
    const { metadata } = props;
    const allTopics = metadata.filter((item: MetaItem) => item.category.toLowerCase() === 'topic');
    return allTopics.map((item: MetaItem) => item.name).join(' · ');
  };

  // Check if membership is higher than Foundation (Performance, Growth, or Accelerator)
  const isPaidMembership = ['Performance', 'Growth', 'Accelerator']
    .includes(props.lowestMembershipRequired ?? '');

  return (
    <>
      <Grid
        container
        direction="column"
        sx={{
          gap: 1,
        }}
      >
        {/* Metadata */}
        <Grid item sx={{
          width: props.categoryText?.toUpperCase() === 'POLL' ? 'fit-content' : '100%',
        }}>
          {props.categoryText?.toUpperCase() === 'POLL' ?
            <Box sx={{
              display: 'flex',
              backgroundColor: indigo[50],
              border: `1px solid ${indigo[200]}`,
              borderRadius: theme.spacing(10),
              p: `${theme.spacing(0.25)} ${theme.spacing(1.5)}`,
              alignItems: 'center',
            }}>
              <Typography
                variant='h10'
                sx={{
                  fontSize: `${theme.spacing(1.75)} !important`,
                  borderRadius: theme.spacing(2),
                  color: indigo[700],
                  textTransform: 'capitalize',
                }}
                component="div">
                {props.categoryText}
              </Typography>
            </Box> : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {props.metadata && getMetaData() && (
                  <Typography
                    variant="h6"
                    sx={{
                      lineHeight: theme.spacing(2.5),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...(!isDesktopScreen && {
                        textWrap: 'auto',
                      }),
                    }}
                  >
                    {props.metadata && getMetaData()}
                  </Typography>
                )}
                {isPaidMembership && (
                  <>
                    {props.metadata && getMetaData() && (
                      <Typography
                        variant="h6"
                        sx={{
                          lineHeight: theme.spacing(2.5),
                          color: theme.palette.grey[500],
                        }}
                      >
                        ·
                      </Typography>
                    )}
                    <Typography
                      data-testid={TestIds.contentFeed.paidMembershipLabel}
                      variant="h6"
                      sx={{
                        lineHeight: theme.spacing(2.5),
                        color: yellow[800],
                        fontWeight: 500,
                      }}
                    >
                      {t('content.paidMembership')}
                    </Typography>
                  </>
                )}
              </Box>
            )
          }
        </Grid>

        {/* Title */}
        {props.contentText &&
          <Grid item>
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.grey[900],
                fontStyle: 'normal',
                lineHeight: theme.spacing(3),
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                ...(props.categoryText?.toUpperCase() !== 'POLL' && {
                  WebkitLineClamp: 2,
                }),
              }}
            >
              {props.contentText}
            </Typography>
          </Grid>
        }

        {/* Description */}
        {props.contentShortDescription &&
          <Grid item>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.grey[700],
                lineHeight: theme.spacing(3),
                display: '-webkit-box',
                WebkitLineClamp: props.categoryText?.toUpperCase() === 'PODCAST' ? 1 : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {props.contentShortDescription}
            </Typography>
          </Grid>
        }
      </Grid >
    </>
  );
}
